import { RangeSlider, type RangeSliderProps } from "@shopify/polaris";
import { useController, useFormContext, type FieldValues, type Path } from "react-hook-form";
import { useVariantFieldDisabled } from "./context/VariantFieldContext";

export interface FormRangeSliderProps<TFieldValues extends FieldValues = FieldValues> extends Omit<
    RangeSliderProps,
    "value" | "onChange" | "error"
> {
    name: Path<TFieldValues> | (string & NonNullable<unknown>);
    label: string;
    suffixTemplate?: (val: number) => string;
    disabled?: boolean;
    /** Override the value read from RHF (e.g. for array-item fields not registered as a real path). */
    value?: number;
    /** Override the write to RHF — receives the new number + the underlying field. */
    onChange?: (value: number, field: ReturnType<typeof useController<TFieldValues>>["field"]) => void;
    /** Optional wrapper className, e.g. for a caller-provided CSS-module override on the Polaris slider. */
    wrapperClassName?: string;
}

export function FormRangeSlider<TFieldValues extends FieldValues = FieldValues>({
    name,
    label,
    suffixTemplate,
    disabled,
    value,
    onChange,
    wrapperClassName,
    ...rest
}: FormRangeSliderProps<TFieldValues>) {
    const { control } = useFormContext<TFieldValues>();
    const {
        field,
        fieldState: { error },
    } = useController<TFieldValues>({ control, name: name as any });

    const val = value !== undefined ? value : Number(field.value ?? 0);
    const isAbDisabled = useVariantFieldDisabled(name as string);

    return (
        <span className={wrapperClassName}>
            <RangeSlider
                {...rest}
                label={label}
                value={val}
                onChange={(newVal) => (onChange ? onChange(Number(newVal), field) : field.onChange(Number(newVal)))}
                output
                suffix={suffixTemplate ? suffixTemplate(val) : `${val}`}
                error={error?.message}
                disabled={disabled || isAbDisabled}
            />
        </span>
    );
}
